import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/student-absenteeism-report/StudentAbsenteeismReportAction";
import {
  ExportButton,
  copy,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  print,
  redirectToPages
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody,
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { useStudentAbsenteeismReportUIContext } from "./StudentAbsenteeismReportUIContext";
import { StudentAbsenteeismReportFilter } from "./student-absenteeism-report-filter/StudentAbsenteeismReportFilter";
import { StudentAbsenteeismReportTable } from "./student-absenteeism-report-table/StudentAbsenteeismReportTable";

export function StudentAbsenteeismReportCard() {
  // Student Absenteeism Report UI Context
  const studentAbsenteeismReportUIContext = useStudentAbsenteeismReportUIContext();
  const studentAbsenteeismReportUIProps = useMemo(() => {
    return {
      queryParams: studentAbsenteeismReportUIContext.queryParams
    };
  }, [studentAbsenteeismReportUIContext]);

  // Getting curret state of Student Absenteeism Report from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.studentAbsenteeismReport }),
    shallowEqual
  );  
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized } = getAuthorities(authorities);

  // Student Absenteeism Report Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    return () => dispatch(actions.cleanStudentsState());
  }, [dispatch]);

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Öğrenci Devamsızlık Raporunu Excele Aktarır */
  const exportStudentAbsenteeismReportToExcel = () => {
    dispatch(actions.getAllStudentAbsenteeismReport(studentAbsenteeismReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [20, 25, 30, 30, 20, 20, 40];
      const columns = ["Şube", "Öğrenci Numarası", "Adı Soyadı", "Veli Adı Soyadı", "Durum", "Tarih", "Açıklama"];

      entities.forEach(entity => rows.push([
        entity.section,
        entity.studentNumber,
        entity.studentName,
        entity.parentName,
        entity.absenteeismStatusName,
        entity.date,
        entity.explanation
      ]));

      const filename = studentAbsenteeismReportUIProps.queryParams.term + " - " + (studentAbsenteeismReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Devamsızlık Raporu";
      const sheetname = "Öğrenci Devamsızlık Raporu"
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  // /** Belli Bir Öğrenci Devamsızlık Raporunu PDF e Aktarır */
  const exportStudentAbsenteeismReportToPDF = () => {
    dispatch(actions.getAllStudentAbsenteeismReport(studentAbsenteeismReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [
        {
          header: "Şube",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Öğrenci Numarası",
          rows: entities.map(entity => entity.studentNumber)
        },
        {
          header: "Adı Soyadı",
          rows: entities.map(entity => entity.studentName)
        },
        {
          header: "Veli Adı Soyadı",
          rows: entities.map(entity => entity.parentName)
        },
        {
          header: "Durum",
          rows: entities.map(entity => entity.absenteeismStatusName)
        },
        {
          header: "Tarih",
          rows: entities.map(entity => entity.date)
        },
        {
          header: "Açıklama",
          rows: entities.map(entity => entity.explanation)
        }
      ];
  
      const filename = studentAbsenteeismReportUIProps.queryParams.term + " - " + (studentAbsenteeismReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Devamsızlık Raporu";
      exportToPDF(views, filename);
    });
  }

  // /** Belli Bir Öğrenci Devamsızlık Raporunu Yazdırma Penceresini Açar */
  const printStudentAbsenteeismReport = () => {
    dispatch(actions.getAllStudentAbsenteeismReport(studentAbsenteeismReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = studentAbsenteeismReportUIProps.queryParams.term + " - " + (studentAbsenteeismReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Devamsızlık Raporu";
      const properties = ["Şube", "Öğrenci Numarası", "Adı Soyadı", "Veli Adı Soyadı", "Durum", "Tarih", "Açıklama"];
  
      entities.forEach(entity => {
        data.push({
          "Şube": entity.section,
          "Öğrenci Numarası": entity.studentNumber,
          "Adı Soyadı": entity.studentName,
          "Veli Adı Soyadı": entity.parentName,
          "Durum": entity.absenteeismStatusName,
          "Tarih": entity.date,
          "Açıklama": entity.explanation
        });
      });
  
      print({ data, properties, header });
    });    
  }

  // /** Belli Bir Öğrenci Devamsızlık Raporunu Panoya Kopyalar */
  const copyStudentAbsenteeismReport = () => {
    dispatch(actions.getAllStudentAbsenteeismReport(studentAbsenteeismReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Şube", "Öğrenci Numarası", "Adı Soyadı", "Veli Adı Soyadı", "Durum", "Tarih", "Açıklama"];
      const header = studentAbsenteeismReportUIProps.queryParams.term + " - " + (studentAbsenteeismReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Devamsızlık Raporu";
      entities.forEach(entity => rows.push([
        entity.section,
        entity.studentNumber,
        entity.studentName,
        entity.parentName,
        entity.absenteeismStatusName,
        entity.date,
        entity.explanation
      ]));
      copy({ header, columns, rows });
    });
  }

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title="Öğrenci Devamsızlık Raporu">
        {
          isExportationAuthorized &&
          <CardHeaderToolbar>
            <ExportButton
              exportToExcel={exportStudentAbsenteeismReportToExcel}
              exportToPDF={exportStudentAbsenteeismReportToPDF}
              print={printStudentAbsenteeismReport}
              copy={copyStudentAbsenteeismReport}
            />
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <StudentAbsenteeismReportFilter />
        <StudentAbsenteeismReportTable />
      </CardBody>
    </Card>
  );
}